import React from 'react';
import { Zap, Phone, Lightbulb, HardHat, FileText } from 'lucide-react';
import { GiWreckingBall } from 'react-icons/gi';

export type PageVisual =
  | 'power'
  | 'telecom'
  | 'lighting'
  | 'lighting-schedule'
  | 'fire-alarm'
  | 'demolition'
  | 'panel-schedule'
  | 'one-line'
  | 'default';

export interface PageVisualConfig {
  type: PageVisual;
  bg: string;
  border: string;
  iconColor: string;
}

const isDarkTheme = () =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

export function getPageVisual(label: string | undefined): PageVisualConfig {
  const lower = (label || '').toLowerCase();
  const dark = isDarkTheme();

  if (lower.includes('one line') || lower.includes('one-line') || lower.includes('single line') || lower.includes('riser')) {
    return { type: 'one-line', bg: '#f1f5f9', border: '#cbd5e1', iconColor: dark ? '#ffffff' : '#475569' };
  }
  if (lower.includes('lighting fixture schedule') || lower.includes('lighting schedule')) {
    return { type: 'lighting-schedule', bg: '#fef9c3', border: '#fde68a', iconColor: '#f59e0b' };
  }
  if (lower.includes('panel schedule')) {
    return { type: 'panel-schedule', bg: '#e0f2fe', border: '#bae6fd', iconColor: dark ? '#ffffff' : '#075985' };
  }
  if (lower.includes('fire alarm')) {
    return { type: 'fire-alarm', bg: '#fee2e2', border: '#fecaca', iconColor: '#e84c1e' };
  }
  if (lower.includes('demolition') || lower.includes('demo')) {
    return { type: 'demolition', bg: '#d1d5db', border: '#9ca3af', iconColor: dark ? '#ffffff' : '#374151' };
  }
  if (lower.includes('low voltage') || lower.includes('telecom') || lower.includes('data')) {
    return { type: 'telecom', bg: '#ede9fe', border: '#ddd6fe', iconColor: dark ? '#ffffff' : '#6d28d9' };
  }
  if (lower.includes('lighting') || lower.includes('reflected ceiling plan') || lower.includes('rcp')) {
    return { type: 'lighting', bg: '#fef3c7', border: '#fde68a', iconColor: '#f59e0b' };
  }
  if (lower.includes('power')) {
    return { type: 'power', bg: '#dcfce7', border: '#bbf7d0', iconColor: '#10b981' };
  }
  return { type: 'default', bg: '#f1f5f9', border: '#cbd5e1', iconColor: dark ? '#ffffff' : '#475569' };
}

const GradientDefs = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <defs>
      <linearGradient id="pis-power-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#34d399" />
      </linearGradient>
      <linearGradient id="pis-lighting-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#fbbf24" />
      </linearGradient>
    </defs>
  </svg>
);

export function renderPageIcon(type: PageVisual, iconColor: string) {
  switch (type) {
    case 'power':
      return (
        <>
          <GradientDefs />
          <Zap size={44} color={iconColor} stroke="url(#pis-power-grad)" strokeWidth={2.4} />
        </>
      );
    case 'telecom':
      return <Phone size={40} color={iconColor} strokeWidth={2.3} />;
    case 'lighting':
      return <Lightbulb size={42} color={iconColor} strokeWidth={2.35} />;
    case 'lighting-schedule':
      return <FileText size={40} color={iconColor} strokeWidth={2.2} />;
    case 'fire-alarm':
      return <HardHat size={42} color={iconColor} strokeWidth={2.3} />;
    case 'demolition':
      return React.createElement(GiWreckingBall as any, { size: 47, color: iconColor });
    case 'panel-schedule':
      return (
        <div
          style={{
            width: '44px',
            height: '34px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridTemplateRows: 'repeat(3, 1fr)',
            border: `1px solid ${iconColor}`,
            opacity: 0.45,
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{ border: `1px solid ${iconColor}` }} />
          ))}
        </div>
      );
    case 'one-line':
      return (
        <svg width="60" height="50" viewBox="0 0 60 50" fill="none">
          {/* Main horizontal bus */}
          <line x1="5" y1="25" x2="55" y2="25" stroke={iconColor} strokeWidth="3" strokeLinecap="round" />
          {/* Vertical branches dropping down */}
          <line x1="14" y1="25" x2="14" y2="42" stroke={iconColor} strokeWidth="2" strokeLinecap="round" />
          <line x1="26" y1="25" x2="26" y2="42" stroke={iconColor} strokeWidth="2" strokeLinecap="round" />
          <line x1="38" y1="25" x2="38" y2="42" stroke={iconColor} strokeWidth="2" strokeLinecap="round" />
          <line x1="50" y1="25" x2="50" y2="42" stroke={iconColor} strokeWidth="2" strokeLinecap="round" />
          {/* Nodes at branch ends */}
          <circle cx="14" cy="44" r="3" fill={iconColor} opacity="0.6" />
          <circle cx="26" cy="44" r="3" fill={iconColor} opacity="0.6" />
          <circle cx="38" cy="44" r="3" fill={iconColor} opacity="0.6" />
          <circle cx="50" cy="44" r="3" fill={iconColor} opacity="0.6" />
          {/* Source node on left */}
          <circle cx="5" cy="25" r="4" fill={iconColor} opacity="0.7" />
        </svg>
      );
    default:
      return <FileText size={40} color={iconColor} strokeWidth={2.2} />;
  }
}
